<template>
	<div class>
		<!-- Modal Component -->
		<ModalBox v-model="legacyModalVisible" title="Edit destination" size="sm" mobile-placement="bottom" scroll-body>
			<div class="px-6 pb-2">
				<div class="flex items-start gap-2 rounded-lg bg-surface-1 ring-1 ring-surface-3 px-3 py-2.5 mb-4">
					<icon-base class="text-surface-7 shrink-0 mt-0.5" width="14" height="14" fill="transparent">
						<icon-link />
					</icon-base>
					<p class="text-tiny text-surface-7 mb-0 leading-snug">
						This is a reusable destination — any changes will apply across all streams it's linked to.
					</p>
				</div>

				<Alert v-if="error" type="error" class="mb-3">
					<div>{{ error }}</div>
				</Alert>

				<!-- Destination name -->
				<label class="block text-sm text-surface-8 mb-1">Destination name</label>
				<FormInput v-model="name" placeholder="Destination name" size="md" class="mb-3" @input="clearError" />

				<!-- Server: dropdown for platforms exposing multiple ingest servers -->
				<template v-if="destinationPlatform && destinationPlatform.servers && destinationPlatform.servers.length > 1">
					<label class="block text-sm text-surface-8 mb-1">Streaming server</label>
					<drop-down-menu type="surface-4" fullWidth size="md" class="mb-3">
						<template #toggle-button>
							{{ selectedServer }}
							<icon-base width-view-box="10" height-view-box="6" iconColor="none" width="10" height="6"
								class="ml-auto">
								<icon-caret-down />
							</icon-base>
						</template>
						<template #menu-items>
							<drop-down-menu-item @click="selectServer(item)" :key="item.id"
								v-for="item in destinationPlatform.servers">
								{{ item.text }}
							</drop-down-menu-item>
						</template>
					</drop-down-menu>
				</template>

				<!-- Server: free-text input for custom / single-server platforms -->
				<template v-else-if="(destinationPlatform && destinationPlatform.customServer) || isCustom">
					<label class="block text-sm text-surface-8 mb-1">{{ isCustom ? 'Server URL' : 'Streaming server' }}</label>
					<FormInput v-model="server" @input="clearError"
						:placeholder="(destinationPlatform && destinationPlatform.serverInputPlaceholder) || 'rtmp://broadcaster_addr/'"
						size="md" class="mb-3" />
				</template>

				<!-- Custom RTMP: optional authentication -->
				<template v-if="isCustom && !isCustomSrt">
					<FormCheckbox class="mb-3" v-model="isAuthRequired" :inputValue="true" name="is-auth-required"
						label="Authentication required" />
					<template v-if="isAuthRequired">
						<label class="block text-sm text-surface-8 mb-1">Username</label>
						<FormInput v-model="username" placeholder="Username" size="md" class="mb-3" />
						<label class="block text-sm text-surface-8 mb-1">Password</label>
						<FormInput type="password" v-model="password" placeholder="Password" size="md" class="mb-3" />
					</template>
				</template>

				<!-- Custom SRT: connection parameters -->
				<template v-else-if="isCustom">
					<label class="block text-sm text-surface-8 mb-1">Port</label>
					<FormInput v-model="port" placeholder="Port" size="md" class="mb-3" />
					<label class="block text-sm text-surface-8 mb-1">Latency</label>
					<FormInput v-model="latency" placeholder="Latency" size="md" class="mb-3" />
					<label class="block text-sm text-surface-8 mb-1">Passphrase</label>
					<FormInput v-model="passphrase" placeholder="Passphrase" size="md" class="mb-3"
						:is-error="!!passphraseError" :helper="passphraseError || undefined" />
					<SrtPidMapping protocol="srt" v-model:pids="pids" v-model:pmtPid="pmtPid"
						:host="server || ''" :port="port || ''" :stream-id="key || ''"
						:latency="latency || ''" :passphrase="passphrase || ''"
						@update:valid="pidsValid = $event" />
				</template>

				<!-- Stream key -->
				<label class="block text-sm text-surface-8 mb-1">
					Stream key <span class="text-surface-6 font-normal">(optional)</span>
				</label>
				<FormInput v-model="key" placeholder="#streaming_key" size="md" />
			</div>

			<template #footer>
				<div class="flex items-center justify-end gap-2 px-6 py-4">
					<FormButton @click="closeModal" type="secondary" size="md">Cancel</FormButton>
					<FormButton @click="updateDestination" type="main" size="md"
						:disabled="(error || !name || !name.trim() || !server || server.length === 0 || (isCustomSrt && (!pidsValid || !!passphraseError))) ? true : false">
						Save changes
					</FormButton>
				</div>
			</template>
		</ModalBox>
	</div>
</template>

<script>
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import { mapGetters } from 'vuex'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'

import DestinationService from "@/services/DestinationService"

import IconBase from '@/components/icon/IconBase.vue'
import IconCaretDown from '@/components/icon/IconCaretDown.vue'
import IconLink from '@/components/icon/IconLink.vue'
import FormButton from '@/components/Atoms/FormButton.vue';
import Alert from '@/components/Atoms/Alert.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue';
import SrtPidMapping from '@/components/ui/SrtPidMapping.vue';
import { srtPassphraseError } from '@/Utils/srtPushTarget';

export default {
	name: 'EditDestinationModal',
	mixins: [legacyModalBridgeMixin],
	legacyModalId: 'edit-destination-modal',
	components: {
		ModalBox,
		DropDownMenu,
		DropDownMenuItem,
		Alert,
		FormButton,
		FormInput,
		FormCheckbox,
		SrtPidMapping,
		IconBase,
		IconCaretDown,
		IconLink
	},
	props: {
		destination: {
			type: Object,
			required: false
		},

	},
	emits: ['updated'],
	data() {
		return {
			name: null,
			server: null,
			key: null,
			mode: null,
			latency: null,
			port: null,
			passphrase: null,
			pids: '',
			pmtPid: '',
			pidsValid: true,
			isAuthRequired: false,
			username: null,
			password: null,
			error: null,
		};
	},
	computed: {
		...mapGetters({
			platforms: "Destination/platforms",
		}),
		destinationPlatform() {
			return this.platforms.find(item => item.name === this.destination?.template)
		},
		isCustom() {
			return this.destination?.template === 'custom'
				? true
				: false
		},
		passphraseError() {
			return this.isCustomSrt ? srtPassphraseError(this.passphrase, { stored: this.destination?.passphrase }) : null
		},
		isCustomSrt() {
			return this.destination?.template === 'custom' && this.destination?.mode === 'srt'
				? true
				: false
		},
		selectedServer() {
			return this.destinationPlatform?.servers?.length > 1
				? this.destinationPlatform?.servers?.find(server => server.value === this.server)?.text
				: this.server
		},
	},
	watch: {
		destination: {
			handler: function (item) {
				this.error = null
				const serverUrl = item?.server?.match(/:\/\/(.*?):(.*?)@/)
				if (serverUrl) {
					this.isAuthRequired = true
					this.username = decodeURIComponent(serverUrl[1])
					this.password = decodeURIComponent(serverUrl[2])
				}
			},
			deep: true
		},
	},
	async mounted() {

	},
	methods: {
		// was @shown="getDestination" / @hidden="clear" on the old b-modal
		onLegacyModalShow() {
			this.getDestination();
		},
		onLegacyModalHidden() {
			this.clear();
		},
		selectServer(item) {
			this.server = item.value
		},
		getDestination() {
			this.name = this.destination.name
			this.key = this.destination.key
			this.server = this.destination.server
			if (this.destination.mode) this.mode = this.destination.mode
			if (this.destination.port) this.port = this.destination.port
			if (this.destination.latency) this.latency = this.destination.latency
			if (this.destination.passphrase) this.passphrase = this.destination.passphrase
			this.pids = this.destination.pids || ''
			this.pmtPid = this.destination.pmtPid || ''
		},
		async updateDestination() {
			const { _id } = this.destination
			const name = (this.name || '').trim()
			if (!name) {
				this.error = 'Name is required'
				return
			}
			try {
				if (this.isCustom && this.isCustomSrt) {
					if (this.server.endsWith('/')) {
						this.server = this.server.replace(/\/$/, '');
					}
					await DestinationService.updateDestination(_id, name, this.server, this.key, this.port, this.latency, this.passphrase, this.pids || '', this.pmtPid || '')
				} else {
					await DestinationService.updateDestination(_id, name, this.server, this.key)
				}
				await this.$store.dispatch('Destination/getDestinations', true) // force refresh after update
				this.$notify({ group: 'success', text: 'Destination changed' })
				this.$emit('updated')
				this.closeModal()
			} catch (error) {
				this.error = error
			}
		},
		clear() {
			this.name = null
			this.server = null
			this.key = null
			this.mode = null
			this.latency = null
			this.port = null
			this.passphrase = null
			this.pids = ''
			this.pmtPid = ''
			this.pidsValid = true
			this.isAuthRequired = false
			this.username = null
			this.password = null
			this.error = null
		},
		clearError() {
			this.error = null
		},
		closeModal() {
			this.hideLegacyModal()
		}
	}
};

</script>

<style scoped></style>
